import { zodResolver as upstream } from '@hookform/resolvers/zod';
import type { FieldValues, Resolver } from 'react-hook-form';
import type { z } from 'zod';

/**
 * Thin wrapper around `@hookform/resolvers/zod`'s `zodResolver`.
 *
 * The resolver package's Zod 4 overload uses `$ZodType` from
 * `zod/v4/core`. In our install, that subpath resolves through the
 * `zod@3.25.76` compat shim (a transitive dep) whose `version.minor`
 * literal is `0`, while the user-land `z` we import comes from
 * `zod@4.3.6` (`version.minor = 3`). TypeScript treats these as two
 * different `$ZodType`s, breaking structural assignability — but at
 * runtime they're identical because the resolver only reads
 * Standard-Schema fields, not the version literal.
 *
 * This wrapper hides the cast behind a typed signature so callers stay
 * fully type-safe. Remove once the zod 3.x compat shim is no longer
 * hoisted (e.g. the transitive dep dropping zod 3.x).
 */
export function zodResolver<TInput extends FieldValues, TOutput>(
  schema: z.ZodType<TOutput, TInput>
): Resolver<TInput, unknown, TOutput> {
  return upstream(schema as never) as Resolver<TInput, unknown, TOutput>;
}
